import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { IDisplayPairing, IFlight, IFullFlight, IGroundEvent, INonFlyingAssignment } from "@vmosolutions/oc-interfaces";
import { cloneDeep } from "lodash";
import { AnyAction } from "redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IEventChip } from "src/components/Flights/EventChip";
import { IFlightChip } from "src/components/Flights/FlightChip";
import {
  findMosaicNode,
  findMosaicNodeByIds,
  findMosaicNodes,
  flattenTree,
  getFirstFullyTypedNodePath,
  getFirstTypedNodePath,
  getFirstValidNodePath,
  IFoundMosaicNode,
  IMosaicNode,
  IMosaicTabNode,
  isLeafNode,
  MosaicDirection,
  SplitDirection,
  updateTabNodeObject,
  updateTreeAddTab,
  updateTreeAppendTabToEnd,
  updateTreeInsertTab,
  updateTreeMoveTabToEnd,
  updateTreeRemoveTab,
  updateTreeSetActiveIndex,
} from "src/utils/MosaicTree";
import { v1 } from "uuid";
import { IRootState } from "../reducers/CombinedReducers";
import { isFlight } from "../reducers/FlightTimelineReducer";
import { ViewId } from "../reducers/MosaicManagerReducer";
import { ActionType } from "./../actions/Helpers";
import { updateUserAppSettings } from "./App.Actions";

export enum Type {
  MOVE_TAB_NODE = "MOVE_TAB_NODE",
  REMOVE_TAB_NODE = "REMOVE_TAB_NODE",
  UPDATE_SPLIT_VIEW_PERCENTAGE = "UPDATE_SPLIT_VIEW_PERCENTAGE",
  APPEND_TAB_NODE = "APPEND_TAB_NODE",
  SET_ACTIVE_INDEX = "SET_ACTIVE_INDEX",
  MOVE_TAB_NODE_TO_END = "MOVE_TAB_NODE_TO_END",
  UPDATE_TAB_NODE = "UPDATE_TAB_NODE",
  SET_DEFAULT_NODE = "SET_DEFAULT_NODE",
}

export interface IMoveTabNode extends AnyAction {
  type: Type.MOVE_TAB_NODE;
  currentNode: IMosaicNode;
}

export interface IRemoveTabNode extends AnyAction {
  type: Type.REMOVE_TAB_NODE;
  currentNode: IMosaicNode;
}

export interface IUpdateSplitViewPercentageState extends AnyAction {
  type: Type.UPDATE_SPLIT_VIEW_PERCENTAGE;
  currentNode: IMosaicNode;
}

export interface IAppendTabNodeState extends AnyAction {
  type: Type.APPEND_TAB_NODE;
  currentNode: IMosaicNode;
}

export interface ISetActiveIndex extends AnyAction {
  type: Type.SET_ACTIVE_INDEX;
  currentNode: IMosaicNode;
}

export interface IAddNodeToFirstValidLeaf extends AnyAction {
  type: Type.APPEND_TAB_NODE;
  currentNode: IMosaicNode;
}

export interface IMoveTabNodeToEnd extends AnyAction {
  type: Type.MOVE_TAB_NODE_TO_END;
  currentNode: IMosaicNode;
}

export interface IUpdateTabNode extends AnyAction {
  type: Type.UPDATE_TAB_NODE;
  currentNode: IMosaicNode;
}

export interface ISetDefaultNode extends AnyAction {
  type: Type.SET_DEFAULT_NODE;
  currentNode: IMosaicNode;
}

export type Action =
  | IMoveTabNode
  | IRemoveTabNode
  | ISetActiveIndex
  | IUpdateSplitViewPercentageState
  | IAppendTabNodeState
  | IAddNodeToFirstValidLeaf
  | IUpdateTabNode
  | ISetDefaultNode
  | AnyAction;

export const appendNodeToFirstValidLeaf = (
  newTabNode: IMosaicTabNode
): ThunkAction<any, object, object, IAddNodeToFirstValidLeaf> => {
  return (dispatch: ThunkDispatch<object, object, IAddNodeToFirstValidLeaf>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    let node: IMosaicNode;
    const typedPath = getFirstTypedNodePath(currentNode, [], newTabNode.type);
    if (typedPath) {
      node = updateTreeAppendTabToEnd(currentNode, typedPath, newTabNode);
    } else {
      const path = getFirstValidNodePath(currentNode, []);
      node = updateTreeAppendTabToEnd(currentNode, [...path], newTabNode);
    }
    dispatch({ type: Type.APPEND_TAB_NODE, currentNode: node });
  };
};

export const moveTabNode = (
  sourceWindowPath: any,
  destWindowPath: any,
  draggedTabNode: IMosaicTabNode,
  hoveredTabNode: IMosaicTabNode
): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const removedNode = updateTreeRemoveTab(currentNode, [...sourceWindowPath], draggedTabNode.id);
    const addedNode = updateTreeAddTab(removedNode, [...destWindowPath], draggedTabNode, hoveredTabNode);
    const node = flattenTree(addedNode);
    dispatch({ type: Type.MOVE_TAB_NODE, currentNode: node });
  };
};

export const moveTabNodeToEnd = (
  sourceWindowPath: string[],
  draggedTabNode: IMosaicTabNode
): ThunkAction<void, IRootState, object, IMoveTabNodeToEnd> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNodeToEnd>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const node = updateTreeMoveTabToEnd(currentNode, [...sourceWindowPath], draggedTabNode);
    dispatch({ type: Type.MOVE_TAB_NODE_TO_END, currentNode: node });
  };
};

export const appendTab = (
  path: string[],
  tabNode: IMosaicTabNode
): ThunkAction<any, object, object, IAppendTabNodeState> => {
  return (dispatch: ThunkDispatch<object, object, IAppendTabNodeState>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const appendedNode = updateTreeAppendTabToEnd(currentNode, [...path], tabNode);
    dispatch({ type: Type.APPEND_TAB_NODE, currentNode: appendedNode });
  };
};

export const removeTabNode = (
  sourceWindowPath: string[],
  tabId: string
): ThunkAction<any, object, object, IRemoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IRemoveTabNode>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const removedNode = updateTreeRemoveTab(currentNode, [...sourceWindowPath], tabId);
    const node = flattenTree(removedNode);
    dispatch({ type: Type.REMOVE_TAB_NODE, currentNode: node });
    if (!node) {
      // @ts-ignore
      dispatch({ type: ActionType.OPEN_LAUNCHPAD });
    }
  };
};

export const insertTabNode = (
  direction: MosaicDirection,
  sourceWindowPath: any,
  destWindowPath: any,
  draggedTabNode: IMosaicTabNode
): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const splitPercentage: number =
      draggedTabNode.preferredSplitPercentage ?? (draggedTabNode.type === "content" ? 50 : 33);
    let node = currentNode;
    if (sourceWindowPath) {
      node = updateTreeRemoveTab(currentNode, [...sourceWindowPath], draggedTabNode.id);
    }
    const inserttedNode = updateTreeInsertTab(node, [...destWindowPath], draggedTabNode, direction, splitPercentage);
    node = flattenTree(inserttedNode);
    dispatch({ type: Type.MOVE_TAB_NODE, currentNode: node });
  };
};

export const setActiveIndex = (
  activeIndex: number,
  path: string[]
): ThunkAction<ISetActiveIndex, object, object, ISetActiveIndex> => {
  return (dispatch: ThunkDispatch<object, object, ISetActiveIndex>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const node = updateTreeSetActiveIndex({ ...currentNode }, [...path], activeIndex);
    const flatten = flattenTree(node);
    dispatch({
      type: Type.SET_ACTIVE_INDEX,
      currentNode: { ...cloneDeep(flatten) },
    });
  };
};

const updateNodeSplitPercentage = (currentNode: any, percentage: number, path: string[]): any => {
  if (currentNode === null) {
    return null;
  }
  // If path is empty then we have reached a leaf node, so try removing the element
  if (path.length === 0 && typeof currentNode === "object") {
    currentNode.splitPercentage = percentage;
    return { ...currentNode };
  }

  // If we are here path is not empty so we need to keep going deeper
  const direction = path.shift();
  if (direction === "first") {
    currentNode.first = updateNodeSplitPercentage(currentNode.first, percentage, path);
  } else {
    currentNode.second = updateNodeSplitPercentage(currentNode.second, percentage, path);
  }

  return { ...currentNode };
};

export const updateSplitViewPercentage = (
  percentage: number,
  path: string[]
): ThunkAction<IUpdateSplitViewPercentageState, object, object, IUpdateSplitViewPercentageState> => {
  return (dispatch: ThunkDispatch<object, object, IUpdateSplitViewPercentageState>, getState: any): any => {
    const currentNode = getState().windowManager.currentNode;
    const updatedNode = updateNodeSplitPercentage(currentNode, percentage, [...path]);
    dispatch({
      type: Type.UPDATE_SPLIT_VIEW_PERCENTAGE,
      currentNode: { ...updatedNode },
    });
  };
};

export const openWindow = (
  title: string,
  componentName: ViewId, //! This should now return a type error if componentName doesn't match a defined window.
  path: string[],
  props: any,
  windowType: "content" | "context" | "singleContext",
  focusExisting?: true,
  position: MosaicDirection = MosaicDirection.Right,
  forcedWidth?: number | string
): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const foundNode: IFoundMosaicNode | null = findMosaicNode(currentNode, [], componentName, props);

    if (focusExisting && foundNode) {
      // OPEN EXISTING WINDOW if one is found
      dispatch(setActiveIndex(foundNode.foundIndex, foundNode.path));
      return null;
    } else {
      // ELSE OPEN A NEW WINDOW
      const newTabNode: IMosaicTabNode = {
        // I wanted to set the id myself, via the app search component, so that I could set the searched crew for that window
        id: props.id ?? v1(),
        componentName,
        title,
        type: windowType,
        props,
        ...(forcedWidth && { preferredSplitPercentage: convertSplitPercentage(forcedWidth, windowType) }),
      };

      let node: IMosaicNode;
      // Grouping policy:
      //   content  -> append into any leaf that already has a content tab (main panel).
      //   context  -> append only into a leaf that is *all* side-panel tabs (context /
      //               singleContext), so side panels gather together and content leaves
      //               are never polluted. Falls through to create a new split otherwise.
      const targetPath =
        windowType === "content"
          ? getFirstTypedNodePath(currentNode, [], "content")
          : getFirstFullyTypedNodePath(currentNode, [], ["context", "singleContext"]);

      if (targetPath !== null) {
        node = updateTreeAppendTabToEnd(currentNode, targetPath, newTabNode);
      } else {
        const splitPercentage = forcedWidth
          ? convertSplitPercentage(forcedWidth, windowType)
          : windowType === "content"
            ? 50
            : 33;

        if (isLeafNode(currentNode) && currentNode.tabNodes.length === 0) {
          const path = getFirstValidNodePath(currentNode, []);
          node = updateTreeAppendTabToEnd(currentNode, [...path], newTabNode);
        } else if (isLeafNode(currentNode)) {
          const path = getFirstValidNodePath(currentNode, []);
          node = updateTreeInsertTab(currentNode, [...path], newTabNode, position, splitPercentage);
        } else {
          // Tree already has splits - root-level split so the new panel gets the correct
          // absolute width instead of a percentage of a nested leaf.
          const newLeaf = {
            id: v1(),
            activeIndex: 0,
            tabNodes: [{ ...newTabNode }],
          };
          if (position === MosaicDirection.Left || position === MosaicDirection.Top) {
            node = {
              first: newLeaf,
              second: currentNode,
              direction: position === MosaicDirection.Left ? SplitDirection.Horizontal : SplitDirection.Vertical,
              splitPercentage: 100 - splitPercentage,
              id: v1(),
            } as IMosaicNode;
          } else {
            node = {
              first: currentNode,
              second: newLeaf,
              direction: position === MosaicDirection.Right ? SplitDirection.Horizontal : SplitDirection.Vertical,
              splitPercentage,
              id: v1(),
            } as IMosaicNode;
          }
        }
      }

      dispatch({ type: Type.MOVE_TAB_NODE, currentNode: node });
      return newTabNode.id;
    }
  };
};

/**
 * Opens the OpsLink window or focuses an existing one and updates its flight context.
 * @param flight - The flight to open/add in OpsLink
 */
export const openOrFocusOpsLinkWindow = (flight: IFlight): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const foundNodes = findMosaicNodes(currentNode, [], "OpsLinkWindow" as ViewId);

    if (foundNodes && foundNodes.length > 0) {
      const foundNode = foundNodes[0];
      const existingTabNode = foundNode.node.tabNodes[foundNode.foundIndex];
      // Use a separate flightToAdd signal so dedup happens against the
      // component's own trackedFlights state — not tab props, which can
      // diverge if the user untracks flights locally.
      const updatedProps = {
        ...existingTabNode.props,
        flightToAdd: { flight, _ts: Date.now() },
      };

      const pathForUpdate = [...foundNode.path];
      const pathForFocus = [...foundNode.path];
      const updatedNode = updateTabNodeObject(currentNode, pathForUpdate, existingTabNode.id, { props: updatedProps });

      // Focus the existing tab and update props in one dispatch
      const finalNode = updateTreeSetActiveIndex(updatedNode, pathForFocus, foundNode.foundIndex);
      dispatch({
        type: Type.MOVE_TAB_NODE,
        currentNode: finalNode,
      });
    } else {
      // No existing OpsLink window found — open a new one
      dispatch(openWindow("OpsLink", "OpsLinkWindow", [], { initialFlights: [flight] }, "content"));
    }
  };
};

const convertSplitPercentage = (forcedWidth: number | string, windowType: string): number => {
  let splitPercentage;
  if (typeof forcedWidth == "number") {
    const windowWidth = window.innerWidth;
    splitPercentage = Math.min(85, Math.max(10, (forcedWidth / windowWidth) * 100));
  } else {
    const splitRegex = new RegExp(/^[0-9]{1,2}%$/i);
    if (splitRegex.exec(forcedWidth)) {
      splitPercentage = Math.min(85, Math.max(10, parseFloat(forcedWidth)));
    } else {
      //backup in case of invalid percentage expression
      splitPercentage = windowType === "content" ? 50 : 33;
    }
  }
  return splitPercentage;
};

export const openMovementMessageWindow = (
  selectedFlights: IFlight[],
  path: string[]
): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    const windowViewId: ViewId = "FlightMovementMessage";
    const windowLabel = "Submit Movement Message";
    const windowProps = { selectedFlight: selectedFlights?.[0] };
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const foundNode: IFoundMosaicNode | null = findMosaicNode(currentNode, [], windowViewId, windowProps);
    if (foundNode) {
      dispatch(setActiveIndex(foundNode.foundIndex, foundNode.path));
    } else {
      dispatch(openWindow(windowLabel, windowViewId, path, windowProps, "context"));
    }
  };
};

export const openSelectedEventWindow = (
  selectedEvent: IFlight | IGroundEvent,
  path: string[]
): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    const windowViewId: ViewId = "BetaEventEditor";
    const windowLabel =
      isFlight(selectedEvent) && !selectedEvent.removalType
        ? "Flight Editor"
        : isFlight(selectedEvent) && !!selectedEvent.removalType
          ? "Canceled Flight Editor"
          : "Ground Event Editor";
    const windowProps = {
      eventId: selectedEvent?._id,
      isFlight: isFlight(selectedEvent),
      aircraftId: selectedEvent?.aircraft?._id,
      ...(isFlight(selectedEvent) && selectedEvent?.removalType ? { removalType: selectedEvent.removalType } : {}),
    };
    dispatch(openWindow(windowLabel, windowViewId, path, windowProps, "singleContext", true));
  };
};

export const flightToFlightChip = (flight: IFlight | IFullFlight): IFlightChip => {
  const { _id, flightNumber, origin, destination, flightType, aircraft, removalType } = flight;
  return { _id, flightNumber, origin, destination, flightType, aircraft, removalType };
};

export const groundEventToEventChip = (groundEvent: IGroundEvent): IEventChip => {
  const { _id, location, aircraft, eventType } = groundEvent;
  return { _id, location, aircraft, eventType };
};
export const openHistoryWindow = (
  title: string,
  props: {
    collection: string;
    id: string;
    //!! passing in the full event is causing issues with the mosaic tabs. They try to match on props excatly :(
    flight?: IFlight;
    pairing?: IDisplayPairing;
    groundEvent?: IGroundEvent;
    assignment?: INonFlyingAssignment;
  },
  path: string[]
): ThunkAction<any, object, object, IMoveTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IMoveTabNode>, getState: any): any => {
    // Check if there is a already a hisotry tab opened for this event id
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const exists = findMosaicNodeByIds(currentNode, "HistoryWindow", props.id);
    if (exists) {
      //Not opening a new history window
      return;
    }
    dispatch(openWindow(title, "HistoryWindow", path, props, "content", true));
  };
};

/**
 * Saves current mosaic layout as user's preferred default in their appSettings
 * @returns -
 */
export const saveMosaicLayout = (reset = false): ThunkAction<any, object, object, any> => {
  return async (dispatch: ThunkDispatch<object, object, any>, getState: any): Promise<any> => {
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    const appSettings = cloneDeep(getState().authentication.appSettings);
    // if it is empty save null
    if (reset || (isLeafNode(currentNode) && currentNode.tabNodes.length === 0)) {
      // localStorage.setItem("mosaic-layout", null);
      const response = await AmplifyAPIWrapper.Put<any>("/me/settings", {
        mosaicLayout: null,
        //blockInfo: appSettings?.blockInfo,
      });
      dispatch(updateUserAppSettings(response, "mosaicLayout"));
      return;
    }
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Saving Current Layout as Default...",
      successMessage: "Default Layout Saved Successfully.",
      errorMessage: "Error: Unable to Save Current Layout",
    };
    const response = await AmplifyAPIWrapper.Put<any>(
      "/me/settings",
      {
        mosaicLayout: currentNode,
        //blockInfo: appSettings?.blockInfo,
      },
      notifications
    );
    dispatch(updateUserAppSettings(response, "mosaicLayout"));
  };
};

export const updateTabNodeByWindowIdAndProps = (
  tabId: string,
  tabNodeUpdate: Partial<IMosaicTabNode>,
  currentWindowProps: any,
  viewId = "PairingEditorWindow" as ViewId
): ThunkAction<any, object, object, IUpdateTabNode> => {
  return (dispatch: ThunkDispatch<object, object, IUpdateTabNode>, getState: any): any => {
    //Is the deep clonign here okay?
    const currentNode = cloneDeep(getState().windowManager.currentNode);
    //get node by window props -> return the window path...
    const foundNode = findMosaicNode(currentNode, [], viewId, currentWindowProps);
    if (!foundNode) {
      console.warn("No node found!!");
    }
    //I think I want it cloned for here! Because I am modifying currentNode?
    // returns the full tree
    const rootNode = updateTabNodeObject(currentNode, foundNode?.path, tabId, tabNodeUpdate);
    dispatch({
      type: Type.UPDATE_TAB_NODE,
      currentNode: rootNode,
    });
  };
};

//Add a mosiac action to update the entire state
export const setDefaultNode = (defaultNode: IMosaicNode): ThunkAction<any, object, object, ISetDefaultNode> => {
  return (dispatch: ThunkDispatch<object, object, ISetDefaultNode>, getState: any): any => {
    dispatch({
      type: Type.SET_DEFAULT_NODE,
      currentNode: defaultNode,
    });
  };
};
